import { useEffect, useRef, useState } from 'react';
import {
  ConversationStatusEvent,
  getConversationStatusHistory,
} from 'data/api/conversation.api';

export interface MemberStatusHistory {
  events: ConversationStatusEvent[];
  /** Background re-read, after a status change and on the inbox poll. */
  refresh: () => void;
}

// Owner-checked at read time, like useMemberTriages: the page re-renders
// under a newly selected family before effects run, and one frame of another
// family's history (names of who acted on it) is not acceptable.
interface HistoryState {
  owner?: string;
  events: ConversationStatusEvent[];
}

/**
 * The open/snoozed/closed markers for the selected family's thread. A failed
 * read leaves the thread without markers rather than putting an error in the
 * middle of the messages; the next poll tries again.
 */
export const useMemberStatusHistory = (
  memberUserId?: string,
): MemberStatusHistory => {
  const [state, setState] = useState<HistoryState>({ events: [] });
  // Only the latest load or refresh may land.
  const seqRef = useRef(0);
  // A refresh made from an older render's closure (after a mutation that
  // moved the selection on) must read the family selected now, or it would
  // supersede that family's load with a stale one.
  const currentRef = useRef(memberUserId);
  currentRef.current = memberUserId;

  const load = (userId: string) => {
    const seq = ++seqRef.current;
    getConversationStatusHistory(userId)
      .then((events) => {
        if (seq !== seqRef.current) return;
        setState((current) =>
          current.owner === userId &&
          JSON.stringify(current.events) === JSON.stringify(events)
            ? current
            : { owner: userId, events },
        );
      })
      .catch((error) => console.log(error));
  };

  useEffect(() => {
    seqRef.current += 1;
    if (memberUserId) load(memberUserId);
  }, [memberUserId]);

  return {
    events: state.owner === memberUserId ? state.events : [],
    refresh: () => {
      if (currentRef.current) load(currentRef.current);
    },
  };
};
